import { useState, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';
import { useTranslation } from 'react-i18next';

export default function ChatHistoryPage({ onClose, token, onLoadSession }) {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetch('/api/v1/aif/chat/sessions', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(setSessions)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const handleTap = async (session) => {
    try {
      const res = await fetch(`/api/v1/aif/chat/sessions/${session.id}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const messages = data.messages.map(m => ({
        role: m.role,
        content: m.content,
        intent: m.intent,
        timestamp: m.timestamp,
      }));
      onLoadSession(data.session_id, messages);
      onClose();
    } catch { /* ignore */ }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    if (isToday) return t('history.today');
    if (isYesterday) return t('history.yesterday');
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  };

  return (
    <div className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col animate-slideInRight">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
        </button>
        <span className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('menu.chatHistory')}</span>
      </header>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-3 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
            <div className="w-14 h-14 bg-[var(--aif-bg-surface)] rounded-2xl flex items-center justify-center mb-4 border border-[var(--aif-border-default)]">
              <svg className="w-7 h-7 text-[var(--aif-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm text-[var(--aif-text-muted)]">{t('history.noSessions')}</p>
          </div>
        ) : (
          <div className="max-w-lg mx-auto py-2">
            {sessions.map(session => (
              <button
                key={session.id}
                onClick={() => handleTap(session)}
                className="w-full flex items-start gap-3 px-6 py-4 text-left hover:bg-[var(--aif-bg-surface)] transition active:bg-[var(--aif-bg-elevated)]"
              >
                <div className="w-9 h-9 bg-[var(--aif-bg-elevated)] rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-[var(--aif-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--aif-text-primary)] truncate">{session.title || t('history.untitled')}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-[var(--aif-text-muted)]">{formatDate(session.created_at)}</span>
                    <span className="text-xs text-[var(--aif-text-muted)]">&middot;</span>
                    <span className="text-xs text-[var(--aif-text-muted)]">{session.message_count} {t('history.messages')}</span>
                  </div>
                </div>
                <svg className="w-4 h-4 text-[var(--aif-text-muted)] mt-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
